
import { EventEmitter } from 'events';

const Deg2Rad = Math.PI / 180;

export const FilletTypes = Object.freeze({
  None: -1,
  DogBone: 0,
  I: 1,
  H: 2,
  Clockwise: 3,
  CounterClockwise: 4
});

class FilletPosition {
  constructor (point, v0, v1) {
    this.point = point;
    this.v0 = v0;
    this.v1 = v1;
  }
}

export class FilletCandaditePosition extends EventEmitter {
  constructor (a, o, b, index, type = FilletTypes.None) {
    super();
    this.a = a; // prev
    this.o = o; // focus
    this.b = b; // next
    this.index = index;
    this.type = type;
    this.selected = false;
  }

  select () {
    this.selected = true;
    this.emit('select', this);
  }

  unselect () {
    this.selected = false;
    this.emit('select', this);
  }
}

const isClockwise = function (points) {
  let n = points.length;

  let result = 0;
  for (let i = 0; i < n; i++) {
    let p0 = points[i];
    let p1 = points[(i + 1) % n];
    let dx = p1.x - p0.x;
    let dy = p1.y + p0.y;
    result += dx * dy;
  }

  return result > 0;
};

/**
 * 3点の進行方向が時計回りかどうかを判定する
 *
 * 原理：
 * 1. 点bを原点とした時の、点aと点cの位置関係から回転方向を判定
 * 2. 外積の考え方を使用：
 *    - 点aから点bへのベクトル: (a.x - b.x, a.y - b.y)
 *    - 点bから点cへのベクトル: (c.x - b.x, c.y - b.y)
 * 3. これらのベクトルの外積が:
 *    - 正の場合 → 時計回り（右回り）
 *    - 負の場合 → 反時計回り（左回り）
 *
 * 直観的な説明：
 * 中央の点bに立って点aを見た時、点cが右側にある場合は時計回り、
 * 左側にある場合は反時計回りと判定される
 */
const isClockwiseFlow = function (a, b, c) {
  return ((a.x - b.x) * (c.y - b.y) - (a.y - b.y) * (c.x - b.x)) > 0;
};

/**
 * 点が線分上に存在するかを判定する
 * @param {Vector2} point - 判定する点
 * @param {Vector2} lineStart - 線分の開始点
 * @param {Vector2} lineEnd - 線分の終了点
 * @param {number} tolerance - 許容誤差（デフォルト: 0.1）
 * @returns {boolean} 点が線分上にある場合true
 */
const isPointOnLine = function (point, lineStart, lineEnd, tolerance = 0.1) {
  // 線分の長さを計算
  const lineLength = Math.sqrt(
    Math.pow(lineEnd.x - lineStart.x, 2) +
    Math.pow(lineEnd.y - lineStart.y, 2)
  );

  // 点から線分の両端までの距離を計算
  const d1 = Math.sqrt(
    Math.pow(point.x - lineStart.x, 2) +
    Math.pow(point.y - lineStart.y, 2)
  );
  const d2 = Math.sqrt(
    Math.pow(point.x - lineEnd.x, 2) +
    Math.pow(point.y - lineEnd.y, 2)
  );

  // 点から線分の両端までの距離の和が、線分の長さとほぼ等しい場合、
  // その点は線分上にあると判定
  return Math.abs(d1 + d2 - lineLength) <= tolerance;
};

/**
 * 点が外形線上に存在するかを判定する
 * @param {Vector2} point - 判定する点
 * @param {Array<Vector2>} outline - 外形線を構成する点の配列
 * @returns {boolean} 点が外形線上にある場合true
 */
const isPointOnOutline = function (point, outline) {
  for (let i = 0; i < outline.length; i++) {
    const start = outline[i];
    const end = outline[(i + 1) % outline.length];
    if (isPointOnLine(point, start, end)) {
      return true;
    }
  }
  return false;
};

export const computeFilletCandidatePositions = function (options) {
  // パラメータの展開
  const { points, isExterior, isPocket, threshold, outline } = options;
  // 角度のしきい値: デフォルトは135度（ラジアンに変換）
  const th = (threshold === undefined) ? 135 * Deg2Rad : threshold;
  // パス全体が時計回りかどうかを判定
  const clockwise = isClockwise(points);
  // フィレット候補位置を格納する配列
  const candidates = [];
  // パスの各頂点を処理
  for (let i = 0, n = points.length; i < n; i++) {
    // 前の点のインデックスを計算（最初の点の場合は最後の点を参照）
    let prev = (i - 1);
    prev = (prev >= 0) ? prev : (n - 1);
    // 次の点のインデックスを計算（最後の点の場合は最初の点を参照）
    let next = (i + 1) % n;

    // 3つの連続する点を取得
    let p0 = points[prev]; // 前の点
    let p1 = points[i];    // 現在の点（角の頂点）
    let p2 = points[next]; // 次の点

    // 隣接する点間の距離を計算
    let p0top1 = Math.sqrt( Math.pow(p1.x-p0.x, 2) + Math.pow(p1.y-p0.y, 2) );
    let p1top2 = Math.sqrt( Math.pow(p2.x-p1.x, 2) + Math.pow(p2.y-p1.y, 2) );

    // 点が近すぎる場合はフィレット候補から除外（最小距離: 1単位）
    if(p0top1 < 1 || p1top2 < 1){
      continue;
    }

    // 角度の計算
    // v0: p0からp1へのベクトル（正規化）
    let v0 = (new THREE.Vector2()).subVectors(p1, p0).normalize();
    // v1: p1からp2へのベクトル（正規化）
    let v1 = (new THREE.Vector2()).subVectors(p2, p1).normalize();
    // 2つのベクトル間の角度を計算（内積からacos）
    let angle = Math.acos(v0.dot(v1));
    // 内角を外角に変換（180度 - 角度）
    angle = Math.PI - angle;

    // 角の回転方向を判定（時計回りか反時計回りか）
    const clockwiseCorner = isClockwiseFlow(p0, p1, p2);

    // 角度がしきい値以下の場合、フィレット候補として処理
   if (angle <= th) {
     let a = points[prev];
     let o = points[i];
     let b = points[next];

     // ポケットの場合、外形線との接点チェックを行う
     if (isPocket && outline && isPointOnOutline(o, outline)) {
       continue; // 外形線上の点はフィレット候補から除外
     }

     if (isExterior) {
        // 外周の場合
        if (
          // 通常の外周: パス全体の回転方向と角の回転方向が異なる場合
          (!isPocket && clockwise !== clockwiseCorner) ||
          // ポケットの外周: パス全体の回転方向と角の回転方向が同じ場合
          (isPocket && clockwise === clockwiseCorner)
        ) {
          // フィレット候補位置を作成して追加
          const cand = new FilletCandaditePosition(a, o, b, candidates.length);
          candidates.push(cand);
        }
      } else {
        // 内周の場合
        // パス全体の回転方向と角の回転方向が同じ場合にフィレット候補として追加
        if (clockwise === clockwiseCorner) {
          const cand = new FilletCandaditePosition(a, o, b, candidates.length);
          candidates.push(cand);
        }
      }
    }
  }

  return candidates;
};



// WEBPACK FOOTER //
// ./src/assets/javascripts/fillet/fillet_utils.js